import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import landingApi from "../../contexts/landingApiClient";
import formatCurrency from "../../utils/formatCurrency";

const CommissionListTable = ({
  affiliateId,
  pageSize: initialPageSize = 5,
}) => {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);

  useEffect(() => {
    const fetchCommissions = async () => {
      if (!affiliateId) return;
      setIsFetching(true);
      try {
        const response = await landingApi.get(`/commissions`, {
          params: {
            "filters[affiliate][id][$eq]": affiliateId,
            "pagination[page]": page + 1,
            "pagination[pageSize]": rowsPerPage,
            populate: "*",
          },
        });
        setData(response.data.data);
        setPagination(response.data.meta.pagination);
      } catch (error) {
        console.error(t("error_fetch_commissions"), error);
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
    };

    fetchCommissions();
  }, [affiliateId, page, rowsPerPage]);

  const tableRows = useMemo(() => {
    return data.map((item) => {
      const attrs = item.attributes;
      return {
        id: item.id,
        date: attrs.createdAt,
        businessEmail: attrs.businessEmail || "-",
        companyName: attrs.companyName || "-",
        locationNames: attrs.locationNames || "-",
        commissionEvent: attrs.commissionEvent || "-",
        commissionAmount: attrs.commissionAmount || 0,
        commissionPercentage: attrs.commissionPercentage || 0,
        subscriptionAmount: attrs.subscriptionAmount || 0,
      };
    });
  }, [data]);

  const showSkeleton = loading && data.length === 0;

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ p: 3, position: "relative", mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t("referral_activity")}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {isFetching && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("date")}</TableCell>
              <TableCell>{t("business_email")}</TableCell>
              <TableCell sx={{ maxWidth: 180 }}>{t("company")}</TableCell>
              <TableCell sx={{ maxWidth: 180 }}>{t("locations")}</TableCell>
              <TableCell>{t("event")}</TableCell>
              <TableCell>{t("commission")}</TableCell>
              <TableCell>{t("commission_rate")}</TableCell>
              <TableCell>{t("subscription")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton ? (
              Array.from(new Array(rowsPerPage)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))
            ) : tableRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t("no_referral_activity_found")}
                </TableCell>
              </TableRow>
            ) : (
              tableRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {new Date(row.date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>{row.businessEmail}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Tooltip title={row.companyName} arrow>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {row.companyName}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Tooltip title={row.locationNames} arrow>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {row.locationNames}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.commissionEvent}</TableCell>
                  <TableCell
                    sx={{
                      color:
                        row.commissionAmount > 0
                          ? "success.main"
                          : "text.secondary",
                      fontWeight: row.commissionAmount > 0 ? "bold" : "normal",
                    }}
                  >
                    {row.commissionAmount > 0 ? "+" : ""}
                    {formatCurrency(row.commissionAmount)}
                  </TableCell>
                  <TableCell
                    sx={{ color: "primary.main", fontWeight: "medium" }}
                  >
                    {row.commissionPercentage}%
                  </TableCell>
                  <TableCell>
                    {formatCurrency(row.subscriptionAmount)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={pagination.total || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t("rows_per_page")}
      />
    </Paper>
  );
};

export default CommissionListTable;
